import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { FormControl } from '@mui/material';
import { isValid, parseISO } from 'date-fns';
import React from 'react';
import { DatePicker } from '../DatePicker';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getLabelAndValuePairs, getVisibleError, handleWidgetUpdate, isSkipValidate, joinLabels, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { convertValueToDate } from './Utils/date';
import { type } from './types';
const WidgetFormDatePicker = (props) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const value = (_a = props.internalData.value) !== null && _a !== void 0 ? _a : { date: props.getDataStringOrUndefined() };
    const error = getVisibleError(props);
    const formLabelSuffix = getFormLabelSuffix(props);
    const readOnly = props.config.readOnly || ((_b = props.rootParams.formParams) === null || _b === void 0 ? void 0 : _b.readOnly);
    return (React.createElement(FormControl, { fullWidth: true, required: (_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.required, error: !!error, disabled: props.config.disabled },
        React.createElement(DatePicker, { label: formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label, minDate: (_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.minDate, maxDate: (_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.maxDate, required: (_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.required, disabled: props.config.disabled, readOnly: readOnly, fullWidth: true, error: !!error, tabIndex: props.editMode ? -1 : undefined, value: value, onChange: (newValue) => {
                if (readOnly) {
                    return;
                }
                props.setData(newValue.date);
                props.internalData.value = newValue;
                renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
            }, autoFocus: (_g = (!props.editMode && props.config.autoFocus)) !== null && _g !== void 0 ? _g : false }),
        React.createElement(FormHelperErrorDescription, { error: error, description: props.config.description })));
};
const WidgetFormDateEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'Date Picker: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            disabled: true,
            readOnly: true,
            autoFocus: true,
        }), buildValidateTab(props, {
            required: true,
            children: [
                {
                    type: 'form.datePicker',
                    key: 'validate.minDate',
                    label: 'Min Date',
                },
                {
                    type: 'form.text',
                    key: 'validate.minDateMessage',
                    label: 'Min Date Message',
                    emptyAsNull: true,
                },
                {
                    type: 'form.datePicker',
                    key: 'validate.maxDate',
                    label: 'Max Date',
                },
                {
                    type: 'form.text',
                    key: 'validate.maxDateMessage',
                    label: 'Max Date Message',
                    emptyAsNull: true,
                },
            ],
        }), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormDatePicker, Object.assign({}, props))));
};
export const widgetFormDatePicker = {
    type,
    validate: (props) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        if (isSkipValidate(props)) {
            return;
        }
        if (((_a = props.internalData.value) === null || _a === void 0 ? void 0 : _a.invalidDate) != null) {
            return 'Invalid date';
        }
        const value = props.getDataStringOrUndefined();
        if (value != null) {
            const date = parseISO(value);
            if (isValid(date)) {
                const minDate = convertValueToDate((_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.minDate);
                const maxDate = convertValueToDate((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.maxDate);
                if (minDate && isValid(minDate) && date < minDate) {
                    return ((_e = (_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.minDateMessage) !== null && _e !== void 0 ? _e : props.config.label + ' cannot be before ' + minDate.toLocaleDateString()); // TODO format
                }
                if (maxDate && isValid(maxDate) && date > maxDate) {
                    return (_g = (_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.maxDateMessage) !== null && _g !== void 0 ? _g : props.config.label + ' cannot be after ' + maxDate.toLocaleDateString(); // TODO format
                }
            }
            else {
                return 'Invalid date';
            }
        }
        else if (((_h = props.config.validate) === null || _h === void 0 ? void 0 : _h.required) && value == null) {
            return (_j = props.config.validate.requiredMessage) !== null && _j !== void 0 ? _j : (props.config.label ? props.config.label + ' is required' : 'Required');
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    getLabelValueAsTextPairs: (props, labelPrefix) => {
        const value = props.getData();
        if (value != null) {
            const date = convertValueToDate(value);
            if (date) {
                return getLabelAndValuePairs(joinLabels(labelPrefix, props.config.label), date.toLocaleDateString()); // TODO format
            }
        }
        return [];
    },
    component: WidgetFormDatePicker,
    icon: React.createElement(CalendarMonthIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormDateEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'datePicker', label: 'Date Picker' }),
};
